import { useState } from 'react'
import { Link, router, usePage } from '@inertiajs/react'
import { Button } from '@/shared/ui/button'
import { Input } from '@/shared/ui/input'
import { Badge } from '@/shared/ui/badge'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/shared/ui/table'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui/select'
import { SearchableSelect } from '@/shared/ui/searchable-select'
import { Checkbox } from '@/shared/ui/checkbox'
import { Label } from '@/shared/ui/label'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/shared/ui/tooltip'
import { useCan } from '@/shared/hooks/useCan'
import { useDebouncedCallback } from '@/shared/hooks/useDebouncedCallback'
import { Resources, Actions } from '@/shared/rbac'
import { t } from '@/shared/lib/i18n'
import { useBreadcrumbs } from '@/shared/lib/breadcrumbs-context'
import {
  adminUserPath,
  adminUsersPath,
  editAdminUserPath,
  newAdminUserPath,
} from '@/shared/lib/routes'
import type { PaginationMeta } from '@/shared/types/PaginationMeta'
import UsersLoggersTabs from '@/pages/Admin/UsersLoggersTabs'
import { formatTenantDateTime } from '@/shared/lib/tenantDateTime'
import type { SharedProps } from '@/shared/types/SharedProps'

export interface UserRow {
  id: number
  // REBUILD-525: canonical "Last, First (ID:id)" from Web::Admin::UserSerializer#display_name.
  displayName: string
  firstName: string
  lastName: string
  email: string
  jobTitle: string | null
  employeeId: string | null
  role: string | null
  roleDisplayName: string | null
  // REBUILD-552: legacy LabLogs user id (null for rebuild-only users); last-logon timezone
  // abbreviation so the list can show date + time + timezone. Department column dropped (AC1).
  llId: number | null
  lastLogonAt: string | null
  lastLogonTimeZone: string | null
  active: boolean
  disabled: boolean
}

interface SiteOption {
  id: number
  name: string
}

interface Filters {
  search: string
  siteId: number | null
  role: string
  onlyActive: boolean
}

interface NavigateOverrides {
  search?: string
  site_id?: string
  role?: string
  only_active?: boolean
  // REBUILD-553: the debounced search navigation preserves component state so the search
  // input stays mounted and keeps focus across the results refresh. Discrete filter actions
  // (site/role, active toggle) leave this false and fully remount as before.
  preserveState?: boolean
}

interface Props {
  users: UserRow[]
  pagination: PaginationMeta
  sites: SiteOption[]
  filters: Filters
  atLimit: boolean
  [key: string]: unknown
}

const ALL_SENTINEL = 'all'

const ROLE_OPTIONS = () => [
  { value: 'customer_user', label: t('admin.users.roleUser') },
  { value: 'customer_supervisor', label: t('admin.users.roleUserPlus') },
  { value: 'customer_admin', label: t('admin.users.roleAdmin') },
]

// REBUILD-552 (AC4/AC5): render last logon as date + time (tenant 12/24h via formatTenantDateTime)
// followed by the server-provided timezone abbreviation. A user who never logged in shows the
// never-logged-in text.
const formatLastLogon = (
  user: Pick<UserRow, 'lastLogonAt' | 'lastLogonTimeZone'>,
  tenantTimeFormat: number | null | undefined,
): string => {
  if (!user.lastLogonAt) return t('admin.users.show.neverLoggedIn')

  const dateTime = formatTenantDateTime(user.lastLogonAt, tenantTimeFormat)
  return user.lastLogonTimeZone ? `${dateTime} ${user.lastLogonTimeZone}` : dateTime
}

const UserDirectoryPage = () => {
  const { users, pagination, sites, filters, atLimit, tenantTimeFormat } = usePage<
    Props & SharedProps
  >().props
  const canCreateUser = useCan(Resources.ADMIN_USER, Actions.CREATE)
  const canUpdateUser = useCan(Resources.ADMIN_USER, Actions.UPDATE)

  useBreadcrumbs([{ label: t('nav.administration') }, { label: t('nav.users') }])

  const [search, setSearch] = useState(filters.search)
  const [siteId, setSiteId] = useState(filters.siteId ? String(filters.siteId) : ALL_SENTINEL)
  const [role, setRole] = useState(filters.role || ALL_SENTINEL)
  const [onlyActive, setOnlyActive] = useState(filters.onlyActive)

  const navigate = (overrides: NavigateOverrides = {}) => {
    const effectiveSearch = overrides.search ?? search
    const effectiveSiteId = overrides.site_id ?? siteId
    const effectiveRole = overrides.role ?? role
    const effectiveOnlyActive = overrides.only_active ?? onlyActive
    const params: Record<string, string> = {}
    if (effectiveSearch) params.search = effectiveSearch
    if (effectiveSiteId !== ALL_SENTINEL) params.site_id = effectiveSiteId
    if (effectiveRole !== ALL_SENTINEL) params.role = effectiveRole
    params.only_active = effectiveOnlyActive ? 'true' : 'false'
    // REBUILD-553: preserve component state (and scroll) for the live search so Inertia reuses
    // the mounted page instead of remounting it — the search input keeps focus mid-keystroke
    // across the debounced results refresh. Scope the partial reload to list + pagination.
    const preserveState = overrides.preserveState ?? false
    router.visit(adminUsersPath(Object.keys(params).length ? params : {}), {
      preserveState,
      ...(preserveState ? { preserveScroll: true, only: ['users', 'pagination'] } : {}),
    })
  }

  const debouncedNavigate = useDebouncedCallback((overrides: NavigateOverrides) => {
    navigate(overrides)
  }, 400)

  const handleSearchChange = (value: string) => {
    if (value.trim() === '') {
      setSearch('')
      debouncedNavigate.cancel()
      // REBUILD-553: preserveState avoids remounting the page so the search input keeps focus.
      navigate({ search: '', preserveState: true })
      return
    }
    setSearch(value)
    // REBUILD-553: preserveState keeps the input mounted/focused across the debounced refresh.
    debouncedNavigate({ search: value, preserveState: true })
  }

  const activeFilterCount = [
    search,
    siteId !== ALL_SENTINEL,
    role !== ALL_SENTINEL,
    !onlyActive,
  ].filter(Boolean).length

  const clearFilters = () => {
    debouncedNavigate.cancel()
    setSearch('')
    setSiteId(ALL_SENTINEL)
    setRole(ALL_SENTINEL)
    setOnlyActive(true)
    router.visit(adminUsersPath(), { preserveState: false })
  }

  const goToPage = (page: number) => {
    const params = new URLSearchParams(window.location.search)
    params.set('page', String(page))
    router.visit(`${adminUsersPath()}?${params}`, { preserveState: true })
  }

  return (
    <div className="space-y-6">
      <UsersLoggersTabs />

      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">{t('admin.users.title')}</h1>
        {canCreateUser &&
          (atLimit ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <span tabIndex={0}>
                  <Button disabled>{t('admin.users.newButton')}</Button>
                </span>
              </TooltipTrigger>
              <TooltipContent>{t('admin.users.userLimitReached')}</TooltipContent>
            </Tooltip>
          ) : (
            <Button asChild>
              <Link href={newAdminUserPath()}>{t('admin.users.newButton')}</Link>
            </Button>
          ))}
      </div>

      {/* Filters */}
      <div className="space-y-3 rounded-md border p-4">
        <div className="flex flex-wrap items-center gap-3">
          <Input
            placeholder={t('admin.users.searchPlaceholder')}
            value={search}
            onChange={(e) => handleSearchChange(e.target.value)}
            className="w-72"
          />

          <SearchableSelect
            className="w-44"
            value={siteId}
            onValueChange={(value) => {
              setSiteId(value)
              navigate({ site_id: value })
            }}
            placeholder={t('admin.users.allSites')}
            options={[
              { value: ALL_SENTINEL, label: t('admin.users.allSites') },
              ...sites.map((site) => ({ value: String(site.id), label: site.name })),
            ]}
          />

          <Select
            value={role}
            onValueChange={(value) => {
              setRole(value)
              navigate({ role: value })
            }}
          >
            <SelectTrigger className="w-36">
              <SelectValue placeholder={t('admin.users.allRoles')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL_SENTINEL}>{t('admin.users.allRoles')}</SelectItem>
              {ROLE_OPTIONS().map((roleOption) => (
                <SelectItem key={roleOption.value} value={roleOption.value}>
                  {roleOption.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <div className="ml-auto flex items-center gap-2">
            <Checkbox
              id="only-active"
              checked={onlyActive}
              onCheckedChange={(checked) => {
                const value = Boolean(checked)
                setOnlyActive(value)
                navigate({ only_active: value })
              }}
            />
            <Label htmlFor="only-active" className="cursor-pointer text-sm font-normal">
              {t('admin.users.activeOnly')}
            </Label>
            {activeFilterCount > 0 && (
              <Button
                variant="ghost"
                size="sm"
                className="ml-2 text-muted-foreground"
                onClick={clearFilters}
              >
                {t('admin.users.clearFilters')}
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Mobile: card list */}
      <div className="space-y-2 lg:hidden">
        {users.length === 0 ? (
          <div className="flex flex-col items-center gap-3 py-16 text-center">
            <p className="text-sm text-muted-foreground">{t('admin.users.table.empty')}</p>
          </div>
        ) : (
          users.map((user) => (
            <div
              key={`user-${user.id}`}
              role="link"
              tabIndex={0}
              onClick={() => router.visit(adminUserPath(user.id))}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  router.visit(adminUserPath(user.id))
                }
              }}
              className="cursor-pointer rounded-lg border bg-card p-3 shadow-sm space-y-2 hover:bg-muted/50 transition-colors"
            >
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <Link
                    href={adminUserPath(user.id)}
                    className="font-semibold hover:underline"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {user.displayName}
                  </Link>
                  {user.employeeId && (
                    <p className="text-xs text-muted-foreground">{user.employeeId}</p>
                  )}
                </div>
                {user.active ? (
                  <Badge variant="outline" className="shrink-0 text-green-700 border-green-300">
                    {t('admin.users.statusActive')}
                  </Badge>
                ) : (
                  <Badge variant="outline" className="shrink-0 text-red-700 border-red-300">
                    {t('admin.users.statusInactive')}
                  </Badge>
                )}
              </div>
              <div className="flex flex-wrap items-center gap-2">
                {user.roleDisplayName && <Badge variant="secondary">{user.roleDisplayName}</Badge>}
                {(user.llId != null || user.jobTitle) && (
                  <span className="text-xs text-muted-foreground">
                    {[
                      user.llId != null ? `${t('admin.users.table.llId')}: ${user.llId}` : null,
                      user.jobTitle,
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                  </span>
                )}
              </div>
              {canUpdateUser && (
                <div className="flex justify-end" onClick={(e) => e.stopPropagation()}>
                  <Button asChild variant="outline" size="sm">
                    <Link href={editAdminUserPath(user.id)}>{t('admin.users.editButton')}</Link>
                  </Button>
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {/* Desktop: table */}
      <div className="hidden rounded-md border lg:block">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t('admin.users.table.name')}</TableHead>
              <TableHead>{t('admin.users.table.llId')}</TableHead>
              <TableHead>{t('admin.users.table.employeeId')}</TableHead>
              <TableHead>{t('admin.users.table.role')}</TableHead>
              <TableHead>{t('admin.users.table.jobTitle')}</TableHead>
              <TableHead>{t('admin.users.table.lastLogon')}</TableHead>
              <TableHead>{t('admin.users.table.status')}</TableHead>
              {canUpdateUser && (
                <TableHead className="w-[1%] text-right">
                  {t('admin.users.table.actions')}
                </TableHead>
              )}
            </TableRow>
          </TableHeader>
          <TableBody>
            {users.map((user) => (
              <TableRow
                key={`user-${user.id}`}
                className="cursor-pointer hover:bg-muted/50"
                onClick={() => router.visit(adminUserPath(user.id))}
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    router.visit(adminUserPath(user.id))
                  }
                }}
              >
                <TableCell className="font-medium">
                  <Link
                    href={adminUserPath(user.id)}
                    className="hover:underline"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {user.displayName}
                  </Link>
                </TableCell>
                <TableCell>{user.llId ?? '—'}</TableCell>
                <TableCell>{user.employeeId ?? '—'}</TableCell>
                <TableCell>
                  {user.roleDisplayName ? (
                    <Badge variant="secondary">{user.roleDisplayName}</Badge>
                  ) : (
                    <span className="text-muted-foreground text-sm">—</span>
                  )}
                </TableCell>
                <TableCell>{user.jobTitle ?? '—'}</TableCell>
                <TableCell>{formatLastLogon(user, tenantTimeFormat)}</TableCell>
                <TableCell>
                  {user.active ? (
                    <Badge variant="outline" className="text-green-700 border-green-300">
                      {t('admin.users.statusActive')}
                    </Badge>
                  ) : (
                    <Badge variant="outline" className="text-red-700 border-red-300">
                      {t('admin.users.statusInactive')}
                    </Badge>
                  )}
                </TableCell>
                {canUpdateUser && (
                  <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                    <Button asChild variant="outline" size="sm">
                      <Link href={editAdminUserPath(user.id)}>{t('admin.users.editButton')}</Link>
                    </Button>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <div className="flex items-center justify-between text-sm text-muted-foreground">
        <span>
          {t('admin.users.paginationPage')} {pagination.page} {t('admin.users.paginationOf')}{' '}
          {pagination.totalPages} ({pagination.total} {t('admin.users.paginationNoun')})
        </span>
        {pagination.totalPages > 1 && (
          <div className="flex gap-2">
            {pagination.page > 1 && (
              <Button variant="outline" size="sm" onClick={() => goToPage(pagination.page - 1)}>
                {t('admin.users.previous')}
              </Button>
            )}
            {pagination.page < pagination.totalPages && (
              <Button variant="outline" size="sm" onClick={() => goToPage(pagination.page + 1)}>
                {t('admin.users.next')}
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

export default UserDirectoryPage
